import * as React from 'react'
import { styled } from '@mui/material/styles'
import ArrowForwardIosSharpIcon from '@mui/icons-material/ArrowForwardIosSharp'
import MuiAccordion from '@mui/material/Accordion'
import MuiAccordionSummary from '@mui/material/AccordionSummary'
import MuiAccordionDetails from '@mui/material/AccordionDetails'
import Typography from '@mui/material/Typography'
import ProgressCounter from './ProgressCounter'
import { LoadingSkeleton } from '../../style/StyledElement'
import VerifiedIcon from '@mui/icons-material/Verified'
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';

const Accordion = styled((props) => (
  <MuiAccordion disableGutters elevation={0} square {...props} />
))(({ theme }) => ({
  border: `1px solid ${theme.palette.divider}`,
  '&:not(:last-child)': {
    borderBottom: 0,
  },
  '&::before': {
    display: 'none',
  },
}))

const SearchBar = (props) => {
  const {searchTerm, setSearchTerm} = props;

  const handleSearch = (event) => {
    setSearchTerm(event.target.value);
  };

  return (
    <TextField
      label="Search"
      variant="outlined"
      value={searchTerm}
      onChange={handleSearch}
      fullWidth
    />
  );
};

const AccordionSummary = styled((props) => (
  <MuiAccordionSummary
    expandIcon={<ArrowForwardIosSharpIcon sx={{ fontSize: '0.9rem' }} />}
    {...props}
  />
))(({ theme }) => ({
  backgroundColor: 'rgba(0, 0, 0, .03)',
  flexDirection: 'row',
  '& .MuiAccordionSummary-expandIconWrapper.Mui-expanded': {
    transform: 'rotate(90deg)',
  },
  '& .MuiAccordionSummary-content': {
    marginLeft: theme.spacing(1),
  },
}))

const AccordionDetails = styled(MuiAccordionDetails)(({ theme }) => ({
  padding: theme.spacing(2),
  borderTop: '1px solid rgba(0, 0, 0, .125)',
  maxHeight:'25vh', 
  overflowY: 'auto'
}))

export default function Accordition({
  similarityLogs,
  targetPredLogs,
  protienPredLogs,
  molecularDockLogs,
}) {
  // `Calculating Similarity For ${smile}`
  const [expanded, setExpanded] = React.useState(false)
  const [smile, setSmile] = React.useState(localStorage.getItem('smiles'))
  const [displayMessage, setDisplayMessage] = React.useState(true)
  const [reqId, setReqId] = React.useState(localStorage.getItem('request_id'))
  const [searchTerm, setSearchTerm] = React.useState('');
  const loadingMessage = `Calculating Similarity For ${smile}`
  const handleChange = (panel) => (event, newExpanded) => {
    if (displayMessage) {
      return
    }
    setExpanded(newExpanded ? panel : false)
  }

  React.useEffect(() => {
    // if (similarityLogs && Object.keys(similarityLogs).length > 0) {
    //   setDisplayMessage(false) // Hide message when logs are available
    // }
    setDisplayMessage(false)
  }, [similarityLogs])

  React.useEffect(() => {
    setSearchTerm('')
  }, [expanded])

  const renderLogs = (logs, module) => {
    if ( 
      typeof logs !== 'object' ||
      logs === null ||
      Object.keys(logs).length === 0
    ) {
      return <Typography>No logs available</Typography>
    }

    if(module !== 'similarity'){
      const smileGrps = {};

      logs.forEach((log) => {
        const tokens = log.split(" ");
        const smile = tokens.pop();

        if(!smileGrps[smile]){
          smileGrps[smile] = [];
        }

        smileGrps[smile].push(log);
      })

      const filteredGroups = Object.fromEntries(
        Object.entries(smileGrps)
          .filter(([key, value]) => key.startsWith(searchTerm))
      );

      const groupedLogs = Object.entries(filteredGroups).map(([key, value]) => {
        return (
          <Accordion>
            <AccordionSummary
              aria-controls="panel1-1-content"
              id="panel1-1-header"
            >
             {key}
            </AccordionSummary>
            <AccordionDetails>
              {
                value.map((log, index) => {
                  return (
                    <Typography sx={{ fontSize: '12px' }} key={index}>
                      {log} <br />
                      <hr />
                    </Typography>
                  )
                })
              }
            </AccordionDetails>
          </Accordion>
        )
      });

      return (
        <div>
          <SearchBar searchTerm={searchTerm} setSearchTerm={setSearchTerm}/>
          {groupedLogs}
        </div>
      )
    }

    return logs.map((log, index) => {
      //const logMessageWithoutTimestamp = log.replace(/^\[.*?\]\s*/, '')
      return (
        <Typography sx={{ fontSize: '12px' }} key={index}>
          {log} <br />
          <hr />
        </Typography>
      )
    })
  }


  React.useEffect(() => {
    const handleStorageChange = () => {
      setSmile(localStorage.getItem('smiles'))
    }

    // Set the initial smile value
    setSmile(localStorage.getItem('smiles'))

    // Add event listener for storage changes
    window.addEventListener('storage', handleStorageChange)

    // Clean up the event listener on component unmount
    return () => {
      window.removeEventListener('storage', handleStorageChange)
    }
  }, [])


  return (
    <div>
      <Accordion
        expanded={expanded === 'panel1'}
        onChange={handleChange('panel1')}
        sx={{
        }}
      >
        <AccordionSummary aria-controls="panel1d-content" id="panel1d-header">
          <Box sx={{marginTop:'auto', marginBottom:'auto'}}>
            Similarity
          </Box>
          <ProgressCounter logs={similarityLogs}/>
        </AccordionSummary>
        <AccordionDetails>
          <Typography sx={{ fontSize: '10px' }} />
          {renderLogs(similarityLogs, 'similarity')}
        </AccordionDetails>
      </Accordion>
      <Accordion
        expanded={expanded === 'panel2'}
        onChange={handleChange('panel2')}
      >
        <AccordionSummary aria-controls="panel2d-content" id="panel2d-header">
            TargetPred
        </AccordionSummary>
        <AccordionDetails>
          <Typography sx={{ fontSize: '10px' }} />
          {renderLogs(targetPredLogs, 'DTP')}
        </AccordionDetails>
      </Accordion>
      <Accordion
        expanded={expanded === 'panel3'}
        onChange={handleChange('panel3')}
      >
        <AccordionSummary aria-controls="panel3d-content" id="panel3d-header">
            ProteinPred
        </AccordionSummary>
        <AccordionDetails>
          <Typography sx={{ fontSize: '10px' }} />
          {renderLogs(protienPredLogs, 'PSP')}
        </AccordionDetails>
      </Accordion>
      <Accordion
        expanded={expanded === 'panel4'}
        onChange={handleChange('panel4')}
      >
        <AccordionSummary aria-controls="panel4d-content" id="panel4d-header">
            MolDock
        </AccordionSummary>
        <AccordionDetails>
          <Typography sx={{ fontSize: '10px' }} />
          {renderLogs(molecularDockLogs, 'Docking')}
        </AccordionDetails>
      </Accordion>

    </div>
  )
}
